import { putProgress } from '@/lib/api/api-client';
import { journeySummaryFrom } from '@/lib/library/journey-glance';
import type { JourneyProgress, ProgressStore } from './progress-store';

/**
 * Un `ProgressStore` qui écrit en local **et** pousse vers le serveur.
 *
 * # Pourquoi le local reste la source
 *
 * `ProgressStore.load()` est **synchrone**, et ce n'est pas un détail
 * d'implémentation : le parcours lit la progression au moment de construire son
 * état, et écrit à chaque phrase validée. Mettre un aller-retour HTTP dans ce
 * chemin ferait dépendre le jeu de la qualité de la connexion, et rendre
 * l'interface asynchrone ferait rippler tout l'orchestrateur pour un service
 * qui n'en a pas besoin.
 *
 * Le serveur ne sert donc pas à *jouer*, il sert à **changer d'appareil**.
 * Sans lui, ouvrir Virtuosa sur un autre écran repart de zéro — ce qui rendait
 * le compte inutile à qui joue.
 *
 * # Ce que fait ce décorateur
 *
 * Il délègue tout au magasin local, puis pousse la sauvegarde vers le serveur
 * **sans l'attendre**. L'envoi est étalé : le parcours écrit à chaque phrase, et
 * pousser à chaque écriture ferait une requête toutes les quelques secondes
 * pour un état qui ne sert qu'au prochain appareil.
 *
 * Il joint au passage le **résumé** de progression (`journeySummaryFrom`), que
 * le serveur range sans le lire. C'est lui qui fait exister « Là où tu t'es
 * arrêté » : sans résumé poussé par le client, le serveur n'a aucun moyen de
 * savoir qu'un morceau est en cours, et l'écran serait vide.
 *
 * # Un échec réseau ne remonte jamais
 *
 * Comme le magasin local avale déjà les échecs de `localStorage`, pour la même
 * raison : la persistance est un à-côté du parcours, et perdre une écriture ne
 * doit jamais interrompre une session en cours. La copie locale, elle, a
 * réussi — c'est celle qui compte pour continuer à jouer.
 */

/**
 * Délai avant l'envoi effectif. Assez long pour que la rafale d'écritures d'une
 * phrase ne fasse qu'une requête, assez court pour qu'un onglet fermé juste
 * après n'emporte pas la session.
 */
const FLUSH_DELAY_MS = 4000;

export class SyncingProgressStore implements ProgressStore {
  private readonly pending = new Map<string, JourneyProgress>();
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly local: ProgressStore,
    /** Injecté pour les tests ; en production c'est `putProgress`. */
    private readonly push: typeof putProgress = putProgress,
  ) {}

  load(morceauId: string, expectedPhrasePlanHash: string): JourneyProgress | null {
    return this.local.load(morceauId, expectedPhrasePlanHash);
  }

  save(morceauId: string, progress: JourneyProgress): void {
    this.local.save(morceauId, progress);

    // Un seul état en attente par morceau : entre deux envois, seul le dernier
    // compte. Empiler les intermédiaires enverrait un historique dont personne
    // ne fait rien.
    this.pending.set(morceauId, progress);
    this.schedule();
  }

  clear(morceauId: string): void {
    this.local.clear(morceauId);
    // On n'efface pas la copie serveur : effacer localement, c'est repartir de
    // zéro *ici*, pas renoncer à ce qu'on a fait ailleurs. La prochaine
    // sauvegarde écrasera de toute façon.
    this.pending.delete(morceauId);
  }

  /**
   * Envoie tout de suite ce qui attend. À appeler quand la page se cache — un
   * onglet fermé pendant le délai d'étalement emporterait sinon la fin de la
   * session.
   */
  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }

    const batch = [...this.pending.entries()];
    this.pending.clear();

    for (const [morceauId, progress] of batch) {
      // Le résumé est calculé **ici** et pas dans `save` : entre l'écriture et
      // l'envoi, plusieurs états ont pu se succéder, et seul le dernier part.
      // Résumer trop tôt ferait décrire au serveur un document qu'il ne reçoit
      // pas — « phrase 3 sur 19 » sur une charge qui en est à la cinquième.
      const summary = journeySummaryFrom(progress);

      void this.push(
        morceauId,
        progress.schemaVersion,
        progress,
        summary,
      ).catch(() => {
        // Silence délibéré : voir l'en-tête. La copie locale a réussi.
      });
    }
  }

  private schedule(): void {
    if (this.timer !== null) {
      return;
    }

    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, FLUSH_DELAY_MS);
  }
}
